import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Check, Copy, ChevronLeft, ChevronRight, Upload, X, CalendarDays, TrendingUp, TrendingDown, DollarSign, PieChart, BarChart3, Camera, Edit, MessageCircle } from "lucide-react";

const API = `${window.location.origin}/api`;

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const formatMoeda = (v) => `R$ ${(v||0).toFixed(2).replace('.',',')}`;

// Converter código de barras (44 dígitos) para linha digitável (47 dígitos)
function barcode44ToLinhaDigitavel(barcode) {
  if (barcode.length !== 44) return barcode;
  
  // Mod10 - cálculo do dígito verificador
  const mod10 = (bloco) => {
    let soma = 0;
    let mult = 2;
    for (let i = bloco.length - 1; i >= 0; i--) {
      let resultado = parseInt(bloco[i]) * mult;
      if (resultado > 9) resultado = Math.floor(resultado / 10) + (resultado % 10);
      soma += resultado;
      mult = mult === 2 ? 1 : 2;
    }
    const resto = soma % 10;
    return resto === 0 ? 0 : 10 - resto;
  };

  // Barcode: [banco(3)][moeda(1)][dv_geral(1)][fator_venc(4)][valor(10)][campo_livre(25)]
  const banco = barcode.substring(0, 3);
  const moeda = barcode.substring(3, 4);
  const dvGeral = barcode.substring(4, 5);
  const fatorVenc = barcode.substring(5, 9);
  const valor = barcode.substring(9, 19);
  const campoLivre = barcode.substring(19, 44);

  // Campo 1: banco(3) + moeda(1) + campo_livre[0-4](5) + DV1
  const bloco1 = banco + moeda + campoLivre.substring(0, 5);
  const dv1 = mod10(bloco1);
  
  // Campo 2: campo_livre[5-14](10) + DV2
  const bloco2 = campoLivre.substring(5, 15);
  const dv2 = mod10(bloco2);
  
  // Campo 3: campo_livre[15-24](10) + DV3
  const bloco3 = campoLivre.substring(15, 25);
  const dv3 = mod10(bloco3);
  
  // Campo 4: DV geral (já existe no barcode)
  // Campo 5: fator_vencimento(4) + valor(10)
  
  const linha = bloco1.substring(0,5) + '.' + bloco1.substring(5) + dv1 + ' '
    + bloco2.substring(0,5) + '.' + bloco2.substring(5) + dv2 + ' '
    + bloco3.substring(0,5) + '.' + bloco3.substring(5) + dv3 + ' '
    + dvGeral + ' '
    + fatorVenc + valor;
  
  // Retornar só números para salvar
  return linha.replace(/[^\d]/g, '');
}

// Scanner de código de barras via câmera
function BarcodeScanner({ onDetected, onClose }) {
  const scannerRef = useRef(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let scanner = null;
    let mounted = true;

    const startScanner = async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');
        if (!mounted) return;
        
        scanner = new Html5Qrcode("barcode-reader", {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODABAR
          ]
        });

        await scanner.start(
          { facingMode: { exact: "environment" } },
          { 
            fps: 20,
            disableFlip: false,
            videoConstraints: {
              facingMode: { exact: "environment" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              focusMode: "continuous"
            }
          },
          (decodedText) => {
            const numeros = decodedText.replace(/[^\d]/g, '');
            if (numeros.length >= 44) {
              const convertido = barcode44ToLinhaDigitavel(numeros);
              scanner.stop().then(() => { scanner.clear(); }).catch(() => {});
              scanner = null;
              onDetected(convertido || numeros);
            }
          },
          () => {}
        );
      } catch (err) {
        console.error("Camera error:", err);
        if (mounted) setErro("Não foi possível acessar a câmera. Verifique as permissões.");
      }
    };

    startScanner();

    return () => {
      mounted = false;
      if (scanner) {
        scanner.stop().then(() => { scanner.clear(); }).catch(() => {});
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="barcode-scanner">
      <div id="barcode-reader" ref={scannerRef} className="flex-1 w-full" />
      <div className="bg-black/80 px-4 py-3 flex items-center justify-between">
        <p className="text-white text-sm">{erro || "Posicione o código de barras do boleto"}</p>
        <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/20 text-white text-sm font-medium hover:bg-white/30">
          Fechar
        </button>
      </div>
    </div>
  );
}
function SemanaView({ semanaData, diasAbrev, totalRecSemana, totalDespSemana, saldoSemana, onVoltar, onNovaDespesa, togglePaga, copiarCodigo, excluirDespesa, renderDialogDespesa, onOpenScanner, uploadingBoleto }) {
  const [collapsed, setCollapsed] = useState({});

  // Calcular pago vs a pagar geral da semana
  const todasDespesas = semanaData.dias.flatMap(d => d.despesas);
  const totalPago = todasDespesas.filter(d => d.paga).reduce((a, d) => a + d.valor, 0);
  const totalAPagar = todasDespesas.filter(d => !d.paga).reduce((a, d) => a + d.valor, 0);

  const toggleDia = (i) => setCollapsed(prev => ({ ...prev, [i]: !prev[i] }));
  const expandidos = semanaData.dias.filter((_, i) => !collapsed[i]).length;

  return (
    <div className="space-y-5" data-testid="gestao-semana">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onVoltar} data-testid="btn-voltar-mes">
            <ChevronLeft className="h-4 w-4 mr-1" /> Voltar ao Mês
          </Button>
          <h2 className="text-xl font-bold" style={{fontFamily:'Work Sans'}}>
            Semana de {semanaData.dias[0]?.data.split('-').reverse().join('/')} a {semanaData.dias[6]?.data.split('-').reverse().join('/')}
          </h2>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={onNovaDespesa} data-testid="btn-nova-despesa-semana">
          <Plus className="h-4 w-4" /> Nova Despesa
        </Button>
      </div>

      {/* Resumo da Semana */}
      <div className="grid grid-cols-5 gap-3">
        <Card className="border-t-2 border-t-emerald-500 bg-emerald-50/50">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] text-emerald-600 font-medium">Receita Líquida</p>
            <p className="text-base font-bold text-emerald-700">{formatMoeda(totalRecSemana)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-2 border-t-red-500 bg-red-50/50">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] text-red-600 font-medium">Total Despesas</p>
            <p className="text-base font-bold text-red-600">{formatMoeda(totalDespSemana)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-2 border-t-green-500 bg-green-50/50">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] text-green-600 font-medium">Valor Pago</p>
            <p className="text-base font-bold text-green-700">{formatMoeda(totalPago)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-2 border-t-amber-500 bg-amber-50/50">
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] text-amber-600 font-medium">A Pagar</p>
            <p className="text-base font-bold text-amber-700">{formatMoeda(totalAPagar)}</p>
          </CardContent>
        </Card>
        <Card className={`border-t-2 ${saldoSemana >= 0 ? 'border-t-blue-500 bg-blue-50/50' : 'border-t-orange-500 bg-orange-50/50'}`}>
          <CardContent className="pt-3 pb-2 px-3 text-center">
            <p className="text-[10px] text-gray-600 font-medium">Saldo Semana</p>
            <p className={`text-base font-bold ${saldoSemana >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>{formatMoeda(saldoSemana)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Dias minimizados - barra horizontal no topo */}
      {Object.keys(collapsed).some(k => collapsed[k]) && (
        <div className="flex gap-2 flex-wrap">
          {semanaData.dias.map((dia, i) => {
            if (!collapsed[i]) return null;
            const saldo = dia.resumo.saldo;
            const temDespesa = dia.despesas.length > 0;
            const temRecebivel = dia.recebiveis.length > 0;
            const diaNum = dia.data.split('-')[2];
            return (
              <button key={dia.data} onClick={() => setCollapsed(prev => ({ ...prev, [i]: false }))}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 bg-white cursor-pointer hover:border-blue-300 transition-all ${saldo < 0 ? 'border-red-200' : temDespesa ? 'border-gray-200' : 'border-gray-100'}`}
                data-testid={`dia-collapsed-${dia.data}`}>
                <div className="text-center">
                  <p className="text-[9px] tracking-wider font-bold text-gray-400">{diasAbrev[i]}</p>
                  <p className="text-base font-bold text-gray-800">{diaNum}</p>
                </div>
                <p className={`text-xs font-bold ${saldo >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{formatMoeda(saldo)}</p>
                {temDespesa && <div className="w-2 h-2 rounded-full bg-red-400" />}
                {temRecebivel && <div className="w-2 h-2 rounded-full bg-emerald-400" />}
                <ChevronRight className="h-3 w-3 text-gray-300" />
              </button>
            );
          })}
        </div>
      )}

      {/* Dias expandidos - grid 3 colunas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {semanaData.dias.map((dia, i) => {
          if (collapsed[i]) return null;
          const saldo = dia.resumo.saldo;
          const temDespesa = dia.despesas.length > 0;
          const temRecebivel = dia.recebiveis.length > 0;
          const diaNum = dia.data.split('-')[2];
          const temConteudo = temDespesa || temRecebivel;

          return (
            <div key={dia.data} className={`rounded-xl border-2 bg-white flex flex-col overflow-hidden ${saldo < 0 ? 'border-red-200' : temDespesa ? 'border-gray-200' : 'border-gray-100'}`}
              data-testid={`dia-${dia.data}`}>
              
              {/* Cabeçalho do dia com botão minimizar */}
              <div className={`px-3 py-2 flex items-center justify-between ${saldo < 0 ? 'bg-red-50' : temDespesa ? 'bg-gray-50' : 'bg-white'}`}>
                <div className="text-center flex-1">
                  <p className="text-[10px] tracking-wider font-bold text-gray-400">{diasAbrev[i]}</p>
                  <p className="text-xl font-bold text-gray-800">{diaNum}</p>
                </div>
                <button onClick={() => setCollapsed(prev => ({ ...prev, [i]: true }))} className="p-1 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" title="Minimizar" data-testid={`minimizar-${dia.data}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Resumo financeiro */}
              <div className="px-4 py-2.5 border-t border-b bg-gray-50/50 space-y-1">
                {dia.resumo.receita_bruta > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-emerald-600">Entrada Líq.</span>
                    <span className="text-xs font-bold text-emerald-700">{formatMoeda(dia.resumo.receita_liquida)}</span>
                  </div>
                )}
                {dia.resumo.total_despesas > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-red-500">Saída</span>
                    <span className="text-xs font-bold text-red-600">{formatMoeda(dia.resumo.total_despesas)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1 border-t border-dashed border-gray-200">
                  <span className="text-xs font-medium text-gray-500">Saldo</span>
                  <span className={`text-sm font-bold ${saldo >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{formatMoeda(saldo)}</span>
                </div>
              </div>

              {/* Despesas do dia */}
              <div className="flex-1 px-3 py-2 space-y-0 overflow-y-auto" style={{maxHeight: '400px'}}>
                {dia.despesas.map((desp) => (
                  <div key={desp.id}
                    className={`flex items-center gap-2 py-1.5 border-b last:border-b-0 ${desp.paga ? 'opacity-40' : ''} ${desp.entidade === 'PF' ? 'border-orange-100' : 'border-sky-100'}`}
                    data-testid={`despesa-card-${desp.id}`}>
                    
                    <button onClick={() => togglePaga(desp.id, desp.paga)}
                      className={`p-1 rounded flex-shrink-0 ${desp.paga ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500 hover:bg-green-100'}`}
                      data-testid={`toggle-paga-${desp.id}`}>
                      <Check className="h-4 w-4" />
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-semibold truncate">{desp.nome}</span>
                        <span className="text-xs font-bold whitespace-nowrap">{formatMoeda(desp.valor)}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className={`text-[9px] px-1 py-0 rounded ${desp.entidade === 'PF' ? 'bg-orange-100 text-orange-700' : 'bg-sky-100 text-sky-700'}`}>{desp.entidade}</span>
                        <span className={`text-[9px] px-1 py-0 rounded ${desp.classificacao === 'fixo' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{desp.classificacao === 'fixo' ? 'F' : 'V'}</span>
                        {desp.parcela_atual && <span className="text-[9px] px-1 py-0 rounded bg-orange-100 text-orange-700">{desp.parcela_atual}/{desp.total_parcelas}</span>}
                        {desp.categoria && <span className="text-[9px] text-gray-400">{desp.categoria}</span>}
                        {desp.observacoes && <span className="text-[9px] text-gray-400 italic">| {desp.observacoes}</span>}
                      </div>
                    </div>

                    {desp.linha_digitavel && (
                      <button onClick={() => copiarCodigo(desp.linha_digitavel)}
                        className="p-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 flex-shrink-0"
                        data-testid={`copiar-codigo-${desp.id}`}>
                        <Copy className="h-3 w-3" />
                      </button>
                    )}
                    {!desp.linha_digitavel && (
                      <button onClick={() => onOpenScanner(desp.id)}
                        className="p-0.5 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex-shrink-0" title="Ler boleto pela câmera"
                        data-testid={`camera-boleto-${desp.id}`}>
                        <Camera className="h-3 w-3" />
                      </button>
                    )}
                    {uploadingBoleto === desp.id && (
                      <span className="text-[9px] text-amber-600 animate-pulse">Lendo...</span>
                    )}
                    <button onClick={() => excluirDespesa(desp.id)}
                      className="p-0.5 rounded text-red-300 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                      data-testid={`excluir-${desp.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {!temConteudo && (
                  <div className="flex flex-col items-center justify-center py-8 text-gray-300">
                    <CalendarDays className="h-8 w-8 mb-2" />
                    <p className="text-xs">Sem lançamentos</p>
                  </div>
                )}
              </div>

              {/* Recebíveis do dia */}
              {temRecebivel && (
                <div className="px-3 py-2.5 border-t bg-emerald-50/50">
                  <p className="text-[10px] font-bold text-emerald-700 mb-1.5 tracking-wider">RECEBÍVEIS ({dia.recebiveis.length})</p>
                  {dia.recebiveis.slice(0, 5).map(rec => (
                    <div key={rec.id} className="flex justify-between items-center text-xs py-0.5">
                      <span className="text-gray-600 truncate flex-1 mr-2">{rec.cliente_nome}</span>
                      <span className="font-semibold text-emerald-700 whitespace-nowrap">{formatMoeda(rec.valor_total)}</span>
                    </div>
                  ))}
                  {dia.recebiveis.length > 5 && (
                    <p className="text-[10px] text-emerald-500 text-center mt-1">+{dia.recebiveis.length - 5} mais</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {renderDialogDespesa()}
    </div>
  );
}

export default function GestaoFinanceiraPage() {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [dashboard, setDashboard] = useState(null);
  const [despesas, setDespesas] = useState([]);
  const [recebiveis, setRecebiveis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Semana
  const [semanaSelecionada, setSemanaSelecionada] = useState(null);
  const [semanaData, setSemanaData] = useState(null);

  // Categorias do banco
  const [categorias, setCategorias] = useState([]);
  const [showGerenciarCat, setShowGerenciarCat] = useState(false);
  const [novaCatForm, setNovaCatForm] = useState({ nome: '', parcelavel: false });
  const [editCatId, setEditCatId] = useState(null);
  const [editCatNome, setEditCatNome] = useState('');

  // Dialog nova despesa
  const [showNovaDespesa, setShowNovaDespesa] = useState(false);
  const [despForm, setDespForm] = useState({
    valor: '', data_vencimento: '', entidade: 'PJ', classificacao: 'fixo',
    categoria: '', parcelavel: false, total_parcelas: '', linha_digitavel: '', observacoes: '', valor_variavel: false
  });
  const [submitting, setSubmitting] = useState(false);

  const loadCategorias = async () => {
    try {
      const res = await axios.get(`${API}/gestao-financeira/categorias`);
      setCategorias(res.data);
    } catch {}
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, despRes, recRes, catRes] = await Promise.all([
        axios.get(`${API}/gestao-financeira/dashboard?mes=${mes}&ano=${ano}`),
        axios.get(`${API}/gestao-financeira/despesas?mes=${mes}&ano=${ano}`),
        axios.get(`${API}/gestao-financeira/recebimentos?mes=${mes}&ano=${ano}`),
        axios.get(`${API}/gestao-financeira/categorias`)
      ]);
      setDashboard(dashRes.data);
      setDespesas(despRes.data);
      setRecebiveis(recRes.data);
      setCategorias(catRes.data);
    } catch (err) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [mes, ano]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadSemana = async (dataInicio) => {
    try {
      const res = await axios.get(`${API}/gestao-financeira/semana?data_inicio=${dataInicio}`);
      setSemanaData(res.data);
    } catch { toast.error("Erro ao carregar semana"); }
  };

  const [uploadingBoleto, setUploadingBoleto] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerTarget, setScannerTarget] = useState(null); // 'form' ou despesa_id

  const handleUploadBoleto = async (despesaId, file) => {
    if (!file) return;
    setUploadingBoleto(despesaId);
    try {
      const formData = new FormData();
      formData.append('imagem', file);
      const res = await axios.post(`${API}/gestao-financeira/despesas/${despesaId}/boleto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.linha_digitavel) {
        toast.success(`Código extraído!`);
        navigator.clipboard.writeText(res.data.linha_digitavel);
      } else {
        toast.error("Não foi possível extrair a linha digitável.");
      }
      loadDashboard();
      if (semanaData) loadSemana(semanaData.data_inicio);
    } catch {
      toast.error("Erro ao processar boleto");
    } finally {
      setUploadingBoleto(null);
    }
  };

  const handleScanDetected = async (codigo) => {
    setShowScanner(false);
    if (scannerTarget === 'form') {
      setDespForm(prev => ({...prev, linha_digitavel: codigo}));
      toast.success("Código lido com sucesso!");
      setTimeout(() => setShowNovaDespesa(true), 300);
    } else if (scannerTarget) {
      // Salvar na despesa existente
      try {
        await axios.put(`${API}/gestao-financeira/despesas/${scannerTarget}/boleto-codigo`, { linha_digitavel: codigo });
        toast.success("Código salvo!");
        navigator.clipboard.writeText(codigo);
        loadDashboard();
        if (semanaData) loadSemana(semanaData.data_inicio);
      } catch { toast.error("Erro ao salvar código"); }
    }
    setScannerTarget(null);
  };

  const handleCriarDespesa = async () => {
    if (!despForm.categoria || !despForm.valor || !despForm.data_vencimento) {
      toast.error("Selecione a categoria, valor e vencimento"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/gestao-financeira/despesas`, {
        ...despForm,
        nome: despForm.categoria,
        valor: parseFloat(despForm.valor),
        total_parcelas: despForm.parcelavel ? parseInt(despForm.total_parcelas) || 1 : null,
        valor_variavel: despForm.classificacao === 'fixo' ? !!despForm.valor_variavel : false
      });
      toast.success(
        despForm.parcelavel
          ? `${despForm.total_parcelas} parcelas criadas!`
          : (despForm.classificacao === 'fixo' && despForm.valor_variavel
              ? "12 meses criados (valor editável individualmente)"
              : "Despesa criada!")
      );
      setShowNovaDespesa(false);
      setDespForm({ valor: '', data_vencimento: '', entidade: 'PJ', classificacao: 'fixo', categoria: '', parcelavel: false, total_parcelas: '', linha_digitavel: '', observacoes: '', valor_variavel: false });
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar despesa");
    } finally { setSubmitting(false); }
  };

  const [saindoIds, setSaindoIds] = useState(new Set());

  const togglePaga = async (id, atualPaga) => {
    try {
      // Se está marcando como paga (atualPaga=false), anima saída
      if (!atualPaga) {
        const desp = despesas.find(d => d.id === id);
        if (desp) {
          toast.success(`🎉 ${desp.nome} — ${formatMoeda(desp.valor)} paga!`, { duration: 2500 });
          setSaindoIds(prev => new Set(prev).add(id));
          await new Promise(r => setTimeout(r, 450));
        }
      }
      await axios.put(`${API}/gestao-financeira/despesas/${id}/${atualPaga ? 'despagar' : 'pagar'}`);
      await loadDashboard();
      if (semanaData) loadSemana(semanaData.data_inicio);
      setSaindoIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    } catch { toast.error("Erro"); }
  };

  const excluirDespesa = async (id) => {
    try {
      await axios.delete(`${API}/gestao-financeira/despesas/${id}`);
      toast.success("Excluída");
      loadDashboard();
    } catch { toast.error("Erro"); }
  };

  const copiarCodigo = (codigo) => {
    navigator.clipboard.writeText(codigo);
    toast.success("Código copiado!");
  };

  const editarValorDespesa = async (desp) => {
    const atual = (desp.valor || 0).toString().replace('.', ',');
    const novo = window.prompt(`Editar valor de "${desp.nome}"\nData: ${desp.data_vencimento?.split('-').reverse().join('/')}\n\nInforme o novo valor (R$):`, atual);
    if (novo === null) return;
    const valorNum = parseFloat(String(novo).replace(',', '.'));
    if (isNaN(valorNum) || valorNum < 0) { toast.error("Valor inválido"); return; }
    try {
      await axios.patch(`${API}/gestao-financeira/despesas/${desp.id}`, { valor: valorNum });
      toast.success(`Valor atualizado: ${formatMoeda(valorNum)}`);
      loadDashboard();
      if (semanaData) loadSemana(semanaData.data_inicio);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const navMes = (dir) => {
    let m = mes + dir, a = ano;
    if (m > 12) { m = 1; a++; }
    if (m < 1) { m = 12; a--; }
    setMes(m); setAno(a);
    setSemanaSelecionada(null); setSemanaData(null);
  };

  // Formatar data YYYY-MM-DD para DD/MM
  const formatDM = (dt) => dt ? dt.split('-').reverse().slice(0,2).join('/') : '';

  // Gerar e enviar resumo semanal via WhatsApp (semi-automático)
  const enviarResumoWhatsApp = async () => {
    try {
      toast.info("Gerando resumo da semana...");
      const { data } = await axios.get(`${API}/gestao-financeira/resumo-semanal`);

      const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      let msg = `*RESUMO SEMANAL — ${formatDM(data.data_inicio)} a ${formatDM(data.data_fim)}*\n\n`;
      msg += `*Valor de entrada para essa semana:*\nR$ ${fmt(data.entrada_liquida)}\n\n`;

      msg += `*Lista de despesas PF*\n`;
      if (data.despesas_pf.length === 0) {
        msg += `Nenhuma despesa PF nesta semana.\n`;
      } else {
        data.despesas_pf.forEach(d => {
          msg += `${formatDM(d.data_vencimento)} ${d.nome} — R$ ${fmt(d.valor)}\n`;
        });
      }
      msg += `\n*Lista de despesas PJ*\n`;
      if (data.despesas_pj.length === 0) {
        msg += `Nenhuma despesa PJ nesta semana.\n`;
      } else {
        data.despesas_pj.forEach(d => {
          msg += `${formatDM(d.data_vencimento)} ${d.nome} — R$ ${fmt(d.valor)}\n`;
        });
      }

      msg += `\n*Resumo de Despesas*\n`;
      msg += `Total PF: R$ ${fmt(data.total_pf)} (${data.pct_pf.toFixed(1)}%)\n`;
      msg += `Total PJ: R$ ${fmt(data.total_pj)} (${data.pct_pj.toFixed(1)}%)\n`;
      msg += `Total Geral: R$ ${fmt(data.total_despesas)}\n\n`;

      msg += `*Balanço da Semana*\n`;
      msg += `Entrada: R$ ${fmt(data.entrada_liquida)}\n`;
      msg += `- Despesas: R$ ${fmt(data.total_despesas)}\n`;
      msg += `= *R$ ${fmt(data.balanco)}*`;

      const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar resumo semanal");
    }
  };

  // ========= VISÃO SEMANAL =========
  if (semanaSelecionada !== null && semanaData) {
    const diasAbrev = ['DOM','SEG','TER','QUA','QUI','SEX','SÁB'];
    
    // Totais da semana
    const totalRecSemana = semanaData.dias.reduce((a, d) => a + d.resumo.receita_liquida, 0);
    const totalDespSemana = semanaData.dias.reduce((a, d) => a + d.resumo.total_despesas, 0);
    const saldoSemana = totalRecSemana - totalDespSemana;

    return (<>
      <SemanaView
        semanaData={semanaData}
        diasAbrev={diasAbrev}
        totalRecSemana={totalRecSemana}
        totalDespSemana={totalDespSemana}
        saldoSemana={saldoSemana}
        onVoltar={() => { setSemanaSelecionada(null); setSemanaData(null); }}
        onNovaDespesa={() => setShowNovaDespesa(true)}
        togglePaga={togglePaga}
        copiarCodigo={copiarCodigo}
        excluirDespesa={excluirDespesa}
        renderDialogDespesa={renderDialogDespesa}
        onOpenScanner={(target) => { setScannerTarget(target); setShowScanner(true); }}
        uploadingBoleto={uploadingBoleto}
      />
      {showScanner && (
        <BarcodeScanner
          onDetected={handleScanDetected}
          onClose={() => { 
            setShowScanner(false); 
            setScannerTarget(null); 
          }}
        />
      )}
      {renderDialogDespesa()}
    </>);
  }

  // ========= DASHBOARD MENSAL (TELA PRINCIPAL) =========
  const d = dashboard?.despesas || {};
  const r = dashboard?.receitas || {};
  const totalFixas = (d.fixas_pf || 0) + (d.fixas_pj || 0);
  const totalVariaveis = (d.variaveis_pf || 0) + (d.variaveis_pj || 0);
  const pctFixas = d.total > 0 ? (totalFixas / d.total * 100) : 0;

  // Dados PF
  const totalPF = (d.fixas_pf || 0) + (d.variaveis_pf || 0);
  const pctFixasPF = totalPF > 0 ? ((d.fixas_pf || 0) / totalPF * 100) : 0;
  const despesasPF = despesas.filter(x => x.entidade === 'PF');
  const catPF = {};
  despesasPF.forEach(x => { const c = x.categoria || 'Outros'; catPF[c] = (catPF[c] || 0) + x.valor; });
  const pagasPF = despesasPF.filter(x => x.paga).reduce((a, x) => a + x.valor, 0);
  const pendentesPF = totalPF - pagasPF;

  // Dados PJ
  const totalPJ = (d.fixas_pj || 0) + (d.variaveis_pj || 0);
  const pctFixasPJ = totalPJ > 0 ? ((d.fixas_pj || 0) / totalPJ * 100) : 0;
  const despesasPJ = despesas.filter(x => x.entidade === 'PJ');
  const catPJ = {};
  despesasPJ.forEach(x => { const c = x.categoria || 'Outros'; catPJ[c] = (catPJ[c] || 0) + x.valor; });
  const pagasPJ = despesasPJ.filter(x => x.paga).reduce((a, x) => a + x.valor, 0);
  const pendentesPJ = totalPJ - pagasPJ;

  // Alertas de Vencimento (dias até vencer para cada despesa pendente)
  const calcDias = (dataVenc) => {
    if (!dataVenc) return null;
    const hoje = new Date(); hoje.setHours(0,0,0,0);
    const [y, m, dd] = dataVenc.split('-').map(Number);
    const venc = new Date(y, m-1, dd); venc.setHours(0,0,0,0);
    return Math.round((venc - hoje) / 86400000);
  };
  const despPendentes = despesas.filter(x => !x.paga);
  const despVencidas = despPendentes.filter(x => { const d = calcDias(x.data_vencimento); return d !== null && d < 0; });
  const despHoje = despPendentes.filter(x => calcDias(x.data_vencimento) === 0);
  const despAmanha = despPendentes.filter(x => calcDias(x.data_vencimento) === 1);
  const totalVencidas = despVencidas.reduce((a,x) => a + x.valor, 0);
  const totalHoje = despHoje.reduce((a,x) => a + x.valor, 0);
  const totalAmanha = despAmanha.reduce((a,x) => a + x.valor, 0);

  // Mini Pizza component
  const MiniPizza = ({ pctFixo, cor }) => (
    <svg viewBox="0 0 36 36" className="w-16 h-16">
      <circle cx="18" cy="18" r="15.9" fill="transparent" stroke="#e5e7eb" strokeWidth="3"/>
      <circle cx="18" cy="18" r="15.9" fill="transparent" stroke={cor} strokeWidth="3"
        strokeDasharray={`${pctFixo} ${100-pctFixo}`} strokeDashoffset="25" strokeLinecap="round"/>
    </svg>
  );

  // Paleta de cores distintas para o gráfico de categorias
  const PALETA_CATEGORIAS = [
    '#f97316', // laranja
    '#3b82f6', // azul
    '#10b981', // verde
    '#ef4444', // vermelho
    '#8b5cf6', // roxo
    '#ec4899', // rosa
    '#eab308', // amarelo
    '#14b8a6', // teal
    '#6366f1', // indigo
    '#f43f5e', // rose
    '#84cc16', // lime
    '#06b6d4', // cyan
    '#a855f7', // purple
    '#f59e0b', // amber
  ];

  // Pizza real por categoria (multi-fatia, cores distintas)
  const PizzaCategorias = ({ cats, total, size = 96 }) => {
    const entradas = Object.entries(cats).sort((a,b) => b[1]-a[1]);
    if (entradas.length === 0 || total <= 0) {
      return (
        <svg viewBox="0 0 36 36" style={{width: size, height: size}}>
          <circle cx="18" cy="18" r="15.9" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="1"/>
          <text x="18" y="20" textAnchor="middle" fontSize="4" fill="#9ca3af">sem dados</text>
        </svg>
      );
    }
    // Cálculo das fatias como stroke-dasharray (circunferência = 100)
    let offset = 25; // começa no topo
    return (
      <svg viewBox="0 0 36 36" style={{width: size, height: size}}>
        <circle cx="18" cy="18" r="15.9" fill="#fff" stroke="#e5e7eb" strokeWidth="0.3"/>
        {entradas.map(([cat, val], idx) => {
          const pct = (val / total) * 100;
          const cor = PALETA_CATEGORIAS[idx % PALETA_CATEGORIAS.length];
          const circle = (
            <circle key={cat} cx="18" cy="18" r="15.9" fill="transparent"
              stroke={cor} strokeWidth="6"
              strokeDasharray={`${pct} ${100 - pct}`}
              strokeDashoffset={offset}
              transform="rotate(-90 18 18)" />
          );
          offset -= pct;
          return circle;
        })}
      </svg>
    );
  };

  // Mini barras categoria (cada categoria com cor distinta)
  const MiniCategorias = ({ cats, total }) => {
    const entradas = Object.entries(cats).sort((a,b) => b[1]-a[1]);
    return (
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {entradas.map(([cat, val], idx) => {
          const cor = PALETA_CATEGORIAS[idx % PALETA_CATEGORIAS.length];
          return (
            <div key={cat} className="flex items-center gap-1.5 text-xs" data-testid={`cat-bar-${cat}`}>
              <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{backgroundColor: cor}}/>
              <span className="w-14 text-gray-600 truncate">{cat}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="h-full rounded-full" style={{width: `${total > 0 ? (val/total*100) : 0}%`, backgroundColor: cor}}/>
              </div>
              <span className="font-semibold w-16 text-right">{formatMoeda(val)}</span>
            </div>
          );
        })}
        {entradas.length === 0 && <p className="text-[10px] text-gray-400 text-center py-2">Sem dados</p>}
      </div>
    );
  };

  // Helper: dias até o vencimento (negativo = vencida). Retorna null se sem data.
  const diasParaVencer = (dataVenc) => {
    if (!dataVenc) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const [y, m, dd] = dataVenc.split('-').map(Number);
    const venc = new Date(y, m - 1, dd);
    venc.setHours(0, 0, 0, 0);
    return Math.round((venc - hoje) / (1000 * 60 * 60 * 24));
  };

  // Card de despesa individual
  const DespesaItem = ({ desp }) => {
    const dias = desp.paga ? null : diasParaVencer(desp.data_vencimento);
    const vencida = dias !== null && dias < 0;
    const venceHoje = dias === 0;
    const venceAmanha = dias === 1;
    const alerta = vencida || venceHoje || venceAmanha;
    const saindo = saindoIds.has(desp.id);

    const bgClass = desp.paga
      ? (desp.entidade === 'PF' ? 'bg-orange-50 border-orange-200 opacity-50' : 'bg-sky-50 border-sky-200 opacity-50')
      : vencida
        ? 'bg-red-50 border-red-400 border-2'
        : venceHoje
          ? 'bg-yellow-50 border-yellow-400 border-2'
          : venceAmanha
            ? 'bg-orange-50 border-orange-400 border-2'
            : (desp.entidade === 'PF' ? 'bg-orange-50 border-orange-200' : 'bg-sky-50 border-sky-200');

    return (
      <div className={`flex items-center gap-2 p-2 rounded-lg border ${bgClass} transition-all duration-500 ${saindo ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100'}`} data-testid={`despesa-${desp.id}`}>
        <button onClick={() => togglePaga(desp.id, desp.paga)}
          className={`p-1 rounded-full flex-shrink-0 ${desp.paga ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}
          data-testid={`check-${desp.id}`}>
          <Check className="h-3.5 w-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-semibold text-xs truncate">{desp.nome}</p>
            <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${desp.classificacao === 'fixo' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
              {desp.classificacao === 'fixo' ? 'F' : 'V'}
            </span>
            {desp.parcela_atual && <span className="text-[9px] px-1 py-0.5 rounded bg-orange-100 text-orange-700">{desp.parcela_atual}/{desp.total_parcelas}</span>}
            {alerta && vencida && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-red-600 text-white animate-pulse" data-testid={`tarja-vencida-${desp.id}`}>
                VENCIDA {Math.abs(dias)}d
              </span>
            )}
            {alerta && venceHoje && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-yellow-500 text-white" data-testid={`tarja-hoje-${desp.id}`}>
                VENCE HOJE
              </span>
            )}
            {alerta && venceAmanha && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-orange-500 text-white" data-testid={`tarja-amanha-${desp.id}`}>
                VENCE AMANHÃ
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-400">{desp.categoria} | {desp.data_vencimento?.split('-').reverse().join('/')}{desp.observacoes ? ` | ${desp.observacoes}` : ''}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <p className="font-bold text-xs">{formatMoeda(desp.valor)}</p>
          <button onClick={() => editarValorDespesa(desp)} className="p-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200" title="Editar valor" data-testid={`editar-valor-${desp.id}`}>
            <Edit className="h-3 w-3" />
          </button>
          {desp.linha_digitavel && (
            <button onClick={() => copiarCodigo(desp.linha_digitavel)} className="p-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" data-testid={`copiar-${desp.id}`}>
              <Copy className="h-3 w-3" />
            </button>
          )}
          <button onClick={() => excluirDespesa(desp.id)} className="p-1 rounded bg-red-100 text-red-500 hover:bg-red-200">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  };

  function renderDialogDespesa() {
    const categoriasDisponiveis = categorias.filter(c => c.entidade === despForm.entidade && c.classificacao === despForm.classificacao);
    // Categorias para o gerenciador (filtra pela entidade+classificação selecionadas)
    const catGerenciar = categorias.filter(c => c.entidade === despForm.entidade && c.classificacao === despForm.classificacao);

    return (
      <>
      <Dialog open={showNovaDespesa} onOpenChange={setShowNovaDespesa}>
        <DialogContent className="max-w-lg" aria-describedby="nova-despesa-desc">
          <DialogHeader><DialogTitle style={{fontFamily:'Work Sans'}}>Nova Despesa</DialogTitle></DialogHeader>
          <p id="nova-despesa-desc" className="sr-only">Formulário de nova despesa</p>
          <div className="space-y-3">
            {/* Entidade + Classificação */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Entidade *</Label>
                <div className="flex gap-2 mt-1">
                  {['PF','PJ'].map(e => (
                    <Button key={e} type="button" size="sm" variant={despForm.entidade === e ? 'default' : 'outline'}
                      className={`flex-1 ${despForm.entidade === e ? (e === 'PF' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-sky-600 hover:bg-sky-700') : ''}`}
                      onClick={() => setDespForm({...despForm, entidade: e, categoria: '', parcelavel: false})} data-testid={`desp-entidade-${e}`}>
                      {e === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                    </Button>
                  ))}
                </div>
              </div>
              <div><Label>Classificação *</Label>
                <div className="flex gap-2 mt-1">
                  {[['fixo','Fixo'],['variavel','Variável']].map(([v,l]) => (
                    <Button key={v} type="button" size="sm" variant={despForm.classificacao === v ? 'default' : 'outline'}
                      className="flex-1"
                      onClick={() => setDespForm({...despForm, classificacao: v, categoria: '', parcelavel: false, valor_variavel: false})} data-testid={`desp-class-${v}`}>
                      {l}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Categoria + Botão Gerenciar */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Categoria *</Label>
                <button type="button" onClick={() => setShowGerenciarCat(true)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="btn-gerenciar-categorias">
                  Editar categorias
                </button>
              </div>
              <select className="w-full border rounded px-3 py-2 text-sm" value={despForm.categoria}
                onChange={e => {
                  const cat = categoriasDisponiveis.find(c => c.nome === e.target.value);
                  const isVariavel = despForm.classificacao === 'variavel';
                  setDespForm({...despForm, categoria: e.target.value, parcelavel: isVariavel && (cat?.parcelavel || false), total_parcelas: (isVariavel && cat?.parcelavel) ? despForm.total_parcelas : ''});
                }}
                data-testid="desp-categoria">
                <option value="">Selecione...</option>
                {categoriasDisponiveis.map(c => <option key={c.id} value={c.nome}>{c.nome}{c.parcelavel ? ' (parcelável)' : ''}</option>)}
              </select>
            </div>

            {/* Valor + Vencimento */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor *</Label><Input type="number" step="0.01" value={despForm.valor} onChange={e => setDespForm({...despForm, valor: e.target.value})} data-testid="desp-valor" /></div>
              <div><Label>Vencimento *</Label><Input type="date" value={despForm.data_vencimento} onChange={e => setDespForm({...despForm, data_vencimento: e.target.value})} data-testid="desp-vencimento" /></div>
            </div>

            {/* Info recorrência fixa */}
            {despForm.classificacao === 'fixo' && (
              <>
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <CalendarDays className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-blue-700 font-medium">Despesa fixa: será lançada automaticamente por 12 meses no mesmo dia</span>
                </div>
                <label className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg p-3 cursor-pointer select-none" data-testid="desp-valor-variavel-toggle">
                  <input type="checkbox" checked={!!despForm.valor_variavel} onChange={e => setDespForm({...despForm, valor_variavel: e.target.checked})} className="h-4 w-4 accent-purple-600" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-purple-800">Com valor variável</span>
                    <p className="text-[11px] text-purple-600">Ex.: fatura de cartão. Apenas o 1º mês recebe o valor informado. Os demais ficam em R$ 0,00 aguardando edição mensal.</p>
                  </div>
                </label>
              </>
            )}

            {/* Parcelas (só para variável parcelável) */}
            {despForm.classificacao === 'variavel' && despForm.parcelavel && (
              <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
                <span className="text-sm font-medium text-orange-700">Parcelas:</span>
                <Input type="number" min="2" className="w-24" value={despForm.total_parcelas} onChange={e => setDespForm({...despForm, total_parcelas: e.target.value})} placeholder="Qtd" data-testid="desp-parcelas" />
                <span className="text-xs text-orange-600">Lançamentos automáticos nos meses seguintes</span>
              </div>
            )}

            {/* Linha Digitável - oculto para parcelável (boleto é por parcela) */}
            {despForm.classificacao === 'variavel' && despForm.parcelavel ? (
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <Camera className="h-4 w-4 text-gray-500" />
                <span className="text-xs text-gray-600">Boleto será lido individualmente em cada parcela após criação</span>
              </div>
            ) : (
            <div>
              <Label>Linha Digitável (Boleto)</Label>
              <div className="flex gap-2">
                <Input value={despForm.linha_digitavel} onChange={e => setDespForm({...despForm, linha_digitavel: e.target.value})} placeholder="Digite ou use câmera" className="flex-1" data-testid="desp-linha" />
                <button type="button" onClick={() => { setShowNovaDespesa(false); setTimeout(() => { setScannerTarget('form'); setShowScanner(true); }, 300); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100"
                  data-testid="desp-camera-boleto">
                  <Camera className="h-3.5 w-3.5" /> Câmera
                </button>
              </div>
            </div>
            )}

            <div><Label>Observações</Label><Input value={despForm.observacoes} onChange={e => setDespForm({...despForm, observacoes: e.target.value})} data-testid="desp-obs" /></div>

            <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleCriarDespesa} disabled={submitting || !despForm.categoria} data-testid="btn-salvar-despesa">
              {submitting ? 'Salvando...' : despForm.parcelavel ? `Criar ${despForm.total_parcelas || 1} Parcelas` : 'Salvar Despesa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Gerenciar Categorias */}
      <Dialog open={showGerenciarCat} onOpenChange={setShowGerenciarCat}>
        <DialogContent className="max-w-md" aria-describedby="gerenciar-cat-desc">
          <DialogHeader><DialogTitle style={{fontFamily:'Work Sans'}}>Categorias - {despForm.entidade} {despForm.classificacao === 'fixo' ? 'Fixo' : 'Variável'}</DialogTitle></DialogHeader>
          <p id="gerenciar-cat-desc" className="sr-only">Gerenciar categorias</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {catGerenciar.map(c => (
              <div key={c.id} className="flex items-center gap-2 p-2 rounded border bg-gray-50">
                {editCatId === c.id ? (
                  <>
                    <Input value={editCatNome} onChange={e => setEditCatNome(e.target.value)} className="flex-1 h-8 text-sm" autoFocus />
                    <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700" onClick={async () => {
                      if (!editCatNome.trim()) return;
                      await axios.put(`${API}/gestao-financeira/categorias/${c.id}`, { nome: editCatNome.trim() });
                      setEditCatId(null);
                      loadCategorias();
                      toast.success("Atualizada!");
                    }}>
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setEditCatId(null)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{c.nome}</span>
                    {c.parcelavel && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Parcelável</span>}
                    <button onClick={() => { setEditCatId(c.id); setEditCatNome(c.nome); }} className="p-1 rounded hover:bg-gray-200 text-gray-500" data-testid={`edit-cat-${c.id}`}>
                      <Edit className="h-3 w-3" />
                    </button>
                    <button onClick={async () => {
                      await axios.delete(`${API}/gestao-financeira/categorias/${c.id}`);
                      loadCategorias();
                      toast.success("Excluída!");
                    }} className="p-1 rounded hover:bg-red-50 text-red-400">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
            {catGerenciar.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Nenhuma categoria</p>}
          </div>
          {/* Criar nova */}
          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-semibold text-gray-600">Nova Categoria</p>
            <div className="flex gap-2">
              <Input value={novaCatForm.nome} onChange={e => setNovaCatForm({...novaCatForm, nome: e.target.value})} placeholder="Nome da categoria" className="flex-1" data-testid="nova-cat-nome" />
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="checkbox" checked={novaCatForm.parcelavel} onChange={e => setNovaCatForm({...novaCatForm, parcelavel: e.target.checked})} />
                Parcelável
              </label>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="btn-criar-cat" onClick={async () => {
                if (!novaCatForm.nome.trim()) { toast.error("Nome obrigatório"); return; }
                await axios.post(`${API}/gestao-financeira/categorias`, {
                  entidade: despForm.entidade,
                  classificacao: despForm.classificacao,
                  nome: novaCatForm.nome.trim(),
                  parcelavel: novaCatForm.parcelavel
                });
                setNovaCatForm({ nome: '', parcelavel: false });
                loadCategorias();
                toast.success("Categoria criada!");
              }}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestao-financeira-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navMes(-1)} data-testid="btn-mes-anterior"><ChevronLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-bold" style={{fontFamily:'Work Sans'}}>{MESES[mes-1]} {ano}</h1>
          <Button variant="outline" size="sm" onClick={() => navMes(1)} data-testid="btn-mes-proximo"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-green-600 text-green-700 hover:bg-green-50 gap-2" onClick={enviarResumoWhatsApp} data-testid="btn-resumo-whatsapp">
            <MessageCircle className="h-4 w-4" /> Resumo Semanal
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={() => setShowNovaDespesa(true)} data-testid="btn-nova-despesa">
            <Plus className="h-4 w-4" /> Nova Despesa
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400">Carregando...</div>
      ) : (
        <>
          {/* KPIs Geral */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-gray-500 font-medium">Receita Líquida</p>
                <p className="text-xl font-bold text-emerald-700" data-testid="kpi-receita">{formatMoeda(r.liquida)}</p>
                <p className="text-[10px] text-gray-400">Bruta: {formatMoeda(r.bruta)} (-29%)</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-gray-500 font-medium">Total Despesas</p>
                <p className="text-xl font-bold text-red-600" data-testid="kpi-despesas">{formatMoeda(d.total)}</p>
                <p className="text-[10px] text-gray-400">PF: {formatMoeda(totalPF)} | PJ: {formatMoeda(totalPJ)}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${(dashboard?.saldo_projetado||0) >= 0 ? 'border-l-blue-500' : 'border-l-orange-500'}`}>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-gray-500 font-medium">Saldo Projetado</p>
                <p className={`text-xl font-bold ${(dashboard?.saldo_projetado||0) >= 0 ? 'text-blue-700' : 'text-orange-600'}`} data-testid="kpi-saldo">
                  {formatMoeda(dashboard?.saldo_projetado)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-gray-500 font-medium">Pendentes</p>
                <p className="text-xl font-bold text-amber-600" data-testid="kpi-pendentes">{formatMoeda(d.total_pendentes)}</p>
                <p className="text-[10px] text-gray-400">Parcelas ativas: {dashboard?.parcelas_ativas || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Alertas de Vencimento */}
          {(despVencidas.length + despHoje.length + despAmanha.length) > 0 && (
            <Card className="border-2 border-red-300 bg-gradient-to-r from-red-50 via-yellow-50 to-orange-50" data-testid="painel-alertas">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <span className="animate-pulse">⚠️</span>
                  Alertas de Vencimento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {despVencidas.length > 0 && (
                    <div className="bg-red-100 border border-red-400 rounded-lg p-3" data-testid="alerta-vencidas">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-red-700 uppercase tracking-wide">Vencidas</span>
                        <span className="text-xs font-bold bg-red-600 text-white px-2 py-0.5 rounded-full">{despVencidas.length}</span>
                      </div>
                      <p className="text-lg font-bold text-red-700 mb-2">{formatMoeda(totalVencidas)}</p>
                      <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                        {despVencidas.map(x => {
                          const d = calcDias(x.data_vencimento);
                          return (
                            <li key={x.id} className={`bg-white rounded border border-red-200 p-1.5 transition-all duration-500 ${saindoIds.has(x.id) ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100'}`} data-testid={`alerta-item-${x.id}`}>
                              <div className="flex items-center justify-between gap-1">
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] font-semibold text-red-900 truncate">{x.nome}</p>
                                  <p className="text-[9px] text-red-500">{formatMoeda(x.valor)} · {Math.abs(d)}d atrás</p>
                                  {x.observacoes && <p className="text-[9px] text-gray-600 italic mt-0.5">📝 {x.observacoes}</p>}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <button onClick={() => editarValorDespesa(x)} className="p-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200" title="Editar valor" data-testid={`alerta-editar-${x.id}`}>
                                    <Edit className="h-3 w-3" />
                                  </button>
                                  {x.linha_digitavel && (
                                    <button onClick={() => copiarCodigo(x.linha_digitavel)} className="p-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" title="Copiar código do boleto" data-testid={`alerta-copiar-${x.id}`}>
                                      <Copy className="h-3 w-3" />
                                    </button>
                                  )}
                                  <button onClick={() => togglePaga(x.id, false)} className="p-1 rounded bg-green-500 text-white hover:bg-green-600" title="Marcar como paga" data-testid={`alerta-pagar-${x.id}`}>
                                    <Check className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {despHoje.length > 0 && (
                    <div className="bg-yellow-100 border border-yellow-400 rounded-lg p-3" data-testid="alerta-hoje">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-yellow-800 uppercase tracking-wide">Vence Hoje</span>
                        <span className="text-xs font-bold bg-yellow-500 text-white px-2 py-0.5 rounded-full">{despHoje.length}</span>
                      </div>
                      <p className="text-lg font-bold text-yellow-800 mb-2">{formatMoeda(totalHoje)}</p>
                      <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                        {despHoje.map(x => (
                          <li key={x.id} className={`bg-white rounded border border-yellow-300 p-1.5 transition-all duration-500 ${saindoIds.has(x.id) ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100'}`} data-testid={`alerta-item-${x.id}`}>
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold text-yellow-900 truncate">{x.nome}</p>
                                <p className="text-[9px] text-yellow-700">{formatMoeda(x.valor)}</p>
                                {x.observacoes && <p className="text-[9px] text-gray-600 italic mt-0.5">📝 {x.observacoes}</p>}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => editarValorDespesa(x)} className="p-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200" title="Editar valor" data-testid={`alerta-editar-${x.id}`}>
                                  <Edit className="h-3 w-3" />
                                </button>
                                {x.linha_digitavel && (
                                  <button onClick={() => copiarCodigo(x.linha_digitavel)} className="p-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" title="Copiar código do boleto" data-testid={`alerta-copiar-${x.id}`}>
                                    <Copy className="h-3 w-3" />
                                  </button>
                                )}
                                <button onClick={() => togglePaga(x.id, false)} className="p-1 rounded bg-green-500 text-white hover:bg-green-600" title="Marcar como paga" data-testid={`alerta-pagar-${x.id}`}>
                                  <Check className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {despAmanha.length > 0 && (
                    <div className="bg-orange-100 border border-orange-400 rounded-lg p-3" data-testid="alerta-amanha">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-orange-700 uppercase tracking-wide">Vence Amanhã</span>
                        <span className="text-xs font-bold bg-orange-500 text-white px-2 py-0.5 rounded-full">{despAmanha.length}</span>
                      </div>
                      <p className="text-lg font-bold text-orange-700 mb-2">{formatMoeda(totalAmanha)}</p>
                      <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                        {despAmanha.map(x => (
                          <li key={x.id} className={`bg-white rounded border border-orange-300 p-1.5 transition-all duration-500 ${saindoIds.has(x.id) ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100'}`} data-testid={`alerta-item-${x.id}`}>
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold text-orange-900 truncate">{x.nome}</p>
                                <p className="text-[9px] text-orange-700">{formatMoeda(x.valor)}</p>
                                {x.observacoes && <p className="text-[9px] text-gray-600 italic mt-0.5">📝 {x.observacoes}</p>}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => editarValorDespesa(x)} className="p-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200" title="Editar valor" data-testid={`alerta-editar-${x.id}`}>
                                  <Edit className="h-3 w-3" />
                                </button>
                                {x.linha_digitavel && (
                                  <button onClick={() => copiarCodigo(x.linha_digitavel)} className="p-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200" title="Copiar código do boleto" data-testid={`alerta-copiar-${x.id}`}>
                                    <Copy className="h-3 w-3" />
                                  </button>
                                )}
                                <button onClick={() => togglePaga(x.id, false)} className="p-1 rounded bg-green-500 text-white hover:bg-green-600" title="Marcar como paga" data-testid={`alerta-pagar-${x.id}`}>
                                  <Check className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Semanas do Mês - TOPO */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarDays className="h-4 w-4 text-gray-500" />Semanas de {MESES[mes-1]}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {(dashboard?.semanas || []).map((sem, i) => {
                  const saldo = sem.saldo;
                  return (
                    <button key={i} onClick={() => {
                      const dataInicio = sem.data_inicio || `${ano}-${String(mes).padStart(2,'0')}-${String(sem.dia_inicio).padStart(2,'0')}`;
                      setSemanaSelecionada(i);
                      loadSemana(dataInicio);
                    }}
                      className={`p-3 rounded-lg border-2 text-left hover:border-blue-400 transition-colors ${saldo >= 0 ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
                      data-testid={`semana-${i}`}>
                      <p className="text-xs font-semibold text-gray-600">{sem.label || `Dias ${sem.dia_inicio}-${sem.dia_fim}`}</p>
                      <p className="text-xs text-gray-500 mt-1">Entrada: {formatMoeda(sem.receita_liquida)}</p>
                      <p className="text-xs text-gray-500">Saída: {formatMoeda(sem.despesas)}</p>
                      <p className={`text-sm font-bold mt-1 ${saldo >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {formatMoeda(saldo)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Resumo Geral Consolidado */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-gray-500" />Consolidado: Receita vs Despesas</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 items-center">
                {/* Pizza Geral */}
                <div className="flex items-center gap-3">
                  <MiniPizza pctFixo={pctFixas} cor="#3b82f6" />
                  <div className="text-xs space-y-1">
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"/><span>Fixas: {formatMoeda(totalFixas)}</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-300"/><span>Variáveis: {formatMoeda(totalVariaveis)}</span></div>
                  </div>
                </div>
                {/* Barras */}
                <div className="col-span-2">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded bg-emerald-500"/><span>Receita Líquida</span></div>
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded bg-red-400"/><span>Despesas</span></div>
                  </div>
                  <div className="h-12 flex items-end gap-2">
                    {(() => {
                      const max = Math.max(r.liquida || 0, d.total || 0, 1);
                      return (<>
                        <div className="flex-1 flex flex-col items-center gap-0.5">
                          <div className="w-full bg-emerald-500 rounded-t" style={{height: `${((r.liquida||0)/max)*100}%`, minHeight:'4px'}}/>
                          <p className="text-[10px] font-bold text-emerald-700">{formatMoeda(r.liquida)}</p>
                        </div>
                        <div className="flex-1 flex flex-col items-center gap-0.5">
                          <div className="w-full bg-red-400 rounded-t" style={{height: `${((d.total||0)/max)*100}%`, minHeight:'4px'}}/>
                          <p className="text-[10px] font-bold text-red-600">{formatMoeda(d.total)}</p>
                        </div>
                      </>);
                    })()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== PF vs PJ Side by Side ===== */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ---- COLUNA PF ---- */}
            <div className="space-y-4">
              <Card className="border-t-4 border-t-orange-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-orange-500" />Pessoa Física (PF)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* KPIs PF */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-orange-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-orange-600 font-medium">Total</p>
                      <p className="text-sm font-bold text-orange-800" data-testid="pf-total">{formatMoeda(totalPF)}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-green-600 font-medium">Pagas</p>
                      <p className="text-sm font-bold text-green-700">{formatMoeda(pagasPF)}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-amber-600 font-medium">Pendentes</p>
                      <p className="text-sm font-bold text-amber-700">{formatMoeda(pendentesPF)}</p>
                    </div>
                  </div>
                  {/* Pizza PF */}
                  <div className="flex items-center gap-4">
                    <MiniPizza pctFixo={pctFixasPF} cor="#c2410c" />
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-600"/><span>Fixas: {formatMoeda(d.fixas_pf)} ({pctFixasPF.toFixed(0)}%)</span></div>
                      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-300"/><span>Variáveis: {formatMoeda(d.variaveis_pf)} ({(100-pctFixasPF).toFixed(0)}%)</span></div>
                    </div>
                  </div>
                  {/* Categorias PF */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">Mix por Categoria</p>
                    <div className="flex items-center gap-3">
                      <PizzaCategorias cats={catPF} total={totalPF} size={110} />
                      <div className="flex-1 min-w-0"><MiniCategorias cats={catPF} total={totalPF} /></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Lista Despesas PF */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-orange-700">Despesas PF ({despesasPF.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {despesasPF.map(desp => <DespesaItem key={desp.id} desp={desp} />)}
                    {despesasPF.length === 0 && <p className="text-center text-gray-400 text-xs py-4">Sem despesas PF</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ---- COLUNA PJ ---- */}
            <div className="space-y-4">
              <Card className="border-t-4 border-t-sky-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-sky-500" />Pessoa Jurídica (PJ)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* KPIs PJ */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-sky-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-sky-600 font-medium">Total</p>
                      <p className="text-sm font-bold text-sky-800" data-testid="pj-total">{formatMoeda(totalPJ)}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-green-600 font-medium">Pagas</p>
                      <p className="text-sm font-bold text-green-700">{formatMoeda(pagasPJ)}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-amber-600 font-medium">Pendentes</p>
                      <p className="text-sm font-bold text-amber-700">{formatMoeda(pendentesPJ)}</p>
                    </div>
                  </div>
                  {/* Pizza PJ */}
                  <div className="flex items-center gap-4">
                    <MiniPizza pctFixo={pctFixasPJ} cor="#0284c7" />
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-sky-600"/><span>Fixas: {formatMoeda(d.fixas_pj)} ({pctFixasPJ.toFixed(0)}%)</span></div>
                      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-300"/><span>Variáveis: {formatMoeda(d.variaveis_pj)} ({(100-pctFixasPJ).toFixed(0)}%)</span></div>
                    </div>
                  </div>
                  {/* Categorias PJ */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">Mix por Categoria</p>
                    <div className="flex items-center gap-3">
                      <PizzaCategorias cats={catPJ} total={totalPJ} size={110} />
                      <div className="flex-1 min-w-0"><MiniCategorias cats={catPJ} total={totalPJ} /></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Lista Despesas PJ */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-sky-700">Despesas PJ ({despesasPJ.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {despesasPJ.map(desp => <DespesaItem key={desp.id} desp={desp} />)}
                    {despesasPJ.length === 0 && <p className="text-center text-gray-400 text-xs py-4">Sem despesas PJ</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

        </>
      )}

      {showScanner && (
        <BarcodeScanner
          onDetected={handleScanDetected}
          onClose={() => { 
            setShowScanner(false); 
            if (scannerTarget === 'form') setTimeout(() => setShowNovaDespesa(true), 300);
            setScannerTarget(null); 
          }}
        />
      )}
      {renderDialogDespesa()}
    </div>
  );
}
